// Market expansion M6 — the markets transparency surface.
//
// Sibling of /coverage: it states, honestly, exactly which markets
// STRIKZA models AND which it has CONSIDERED AND DECLINED, with the
// reason for each. Non-coverage is a trust asset — the same moat
// philosophy as the league /coverage page. Read-only, anon-safe; no
// model internals are exposed (only WHICH markets are offered or
// declined, never HOW the edge is found).

import { useMarketCoverage } from "../../sportCoverage/useMarketCoverage.ts";

const mono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
};
const eyebrow: React.CSSProperties = {
  ...mono,
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--sa-fg-muted, #9a9aa2)",
};
const card: React.CSSProperties = {
  border: "1px solid var(--sa-hairline, #2a2a30)",
  borderRadius: 6,
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

export function MarketsCoveragePage(): JSX.Element {
  const { overview, loading, error } = useMarketCoverage();

  return (
    <div
      style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18, padding: "20px 24px" }}
      data-page="markets-coverage"
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 26 }}>Markets we model</h1>
        <span style={eyebrow}>
          Every market here is priced from one verifiable model and is
          deterministically settleable. Markets we cannot model
          honestly are not hidden — they are listed below, with the
          reason we decline them. We never publish a price we cannot
          stand behind.
        </span>
      </header>

      {loading ? (
        <div style={card} data-testid="markets-loading">Loading…</div>
      ) : (
        <>
          {error ? (
            <div style={card} data-testid="markets-error">
              Markets coverage temporarily unavailable: {error}
            </div>
          ) : null}

          <section style={card} data-testid="markets-modelled">
            <span style={eyebrow}>Modelled &amp; live</span>
            <div style={{ fontSize: 40, ...mono, color: "var(--sa-accent-positive, #1fbf75)" }}>
              {overview.modelled_total}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {overview.by_family.map((f) => (
                <span
                  key={f.family}
                  data-testid={`mkt-family-${f.family}`}
                  style={{
                    ...mono, fontSize: 11, padding: "2px 8px", borderRadius: 3,
                    border: "1px solid var(--sa-hairline, #2a2a30)",
                    color: "var(--sa-fg-muted, #9a9aa2)",
                  }}
                >
                  {f.family} · {f.n}
                </span>
              ))}
            </div>
          </section>

          <section style={card} data-testid="markets-declined">
            <span style={eyebrow}>
              Considered &amp; declined · {overview.declined.length}
            </span>
            <span style={{ fontSize: 12, color: "var(--sa-fg-muted)" }}>
              We looked at these and chose not to offer them, because we
              cannot price or settle them honestly. Showing them is the
              point — silence would be the dishonest option.
            </span>
            {overview.declined.length === 0 ? (
              <span style={{ color: "var(--sa-fg-muted)" }}>
                Nothing currently declined.
              </span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {overview.declined.map((d) => (
                  <div
                    key={d.canonical_code}
                    data-testid={`mkt-declined-${d.canonical_code}`}
                    style={{
                      display: "flex", flexDirection: "column", gap: 3,
                      borderTop: "1px solid var(--sa-hairline, #2a2a30)",
                      paddingTop: 8,
                    }}
                  >
                    <span style={{ fontSize: 13, color: "var(--sa-fg-primary, #e8e8ea)" }}>
                      {d.label}
                    </span>
                    <span style={{ ...mono, fontSize: 11, color: "var(--sa-fg-muted, #9a9aa2)" }}>
                      {d.reason}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
